import React, { useEffect, useState } from "react";
import * as styles from "./UxScoreForm.module.scss";
import { MdClose } from "react-icons/md";
import { MantineProvider, TextInput } from "@mantine/core";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ButtonComponent from "../Home/ButtonComponent/ButtonComponent";
import { ndeFormInstance } from "../../lib/axios-client";
import posthog from "posthog-js";
import { toast } from "react-toastify";

type FormData = {
  domain: string;
  email: string;
};

const schema = yup.object({
  domain: yup
    .string()
    .required("This field is required!")
    .transform((value) => value.trim())
    .url("Enter a valid URL!"),
  email: yup
    .string()
    .required("Email is required")
    .transform((value) => value.trim())
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Enter a valid email address"),
});

export default function UxScoreForm({
  opened,
  close,
}: {
  opened: boolean;
  close: () => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });


  useEffect(() => {
    if (opened) {
      reset();
      setShowThankYouMessage(false);
    }
  }, [opened, reset]);

  const [apiLoading, setApiLoading] = useState(false);
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);

  const onSubmit = async (data: FormData) => {
    setApiLoading(true);

    try {
      const landing_page = sessionStorage.getItem("landing_page");
      await ndeFormInstance.post("/lead-tracking", {
        ...data,
        form_id: "ux-score-form",
        landing_page,
      });
      if ((window as any).gtag) {
        (window as any).gtag("event", "contact_form_submitted");
      }
      posthog.capture("form_submitted", {
        form_id: "contact_form_submitted",
        submission_status: "success",
      });
      setShowThankYouMessage(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'An error occurred');
      console.error(error);
    } finally {
      setApiLoading(false);
    }
  };

  return (
    <MantineProvider>
      {opened && (
        <div className={styles.UxScoreForm__backdrop}>
          <div className={styles.UxScoreForm__container}>
            <button
              className={styles.UxScoreForm__closeButton}
              onClick={() => {
                close();
                sessionStorage.removeItem("formOpen");
              }}
            >
              Close <MdClose size={20} />
            </button>
            <div className={styles.UxScoreForm__modal}>
              <div className={styles.UxScoreForm__heading}>
                <div className={styles.UxScoreForm__heading_text}>
                    <span className={styles.UxScoreForm__new_text}>FREE</span>
                  Access Your
                  <br />  UX Score Now
                </div>
              </div>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className={styles.UxScoreForm__formContainer}
              >
                <TextInput
                  placeholder="Enter your website URL *"
                  size="lg"
                  className={styles.UxScoreForm__fieldArea}
                  {...register("domain")}
                  error={errors.domain?.message}
                />

                <TextInput
                  placeholder="Your email for contact *"
                  size="lg"
                  className={styles.UxScoreForm__fieldArea}
                  {...register("email")}
                  error={errors.email?.message}
                />
                
                <div className={styles.UxScoreForm__helpText}>
                  💡 <i> Get your results faster by providing an email address matching your domain name. Please note that this free audit covers only the homepage.</i>
                </div>

                <ButtonComponent
                  buttonText={"Submit"}
                  buttonStyle={"solidBg"}
                  type="submit"
                  className={styles.UxScoreForm__submit_button}
                  onClick={undefined}
                  disabled={undefined}
                  loading={apiLoading}
                />
              </form>
              {showThankYouMessage && (
                <div className={styles.UxScoreForm__thank_you}>
                  <span className={styles.UxScoreForm__text_style}>
                    Thanks! Sit tight -
                  </span>{" "}
                  your custom UX report will <br /> be in your inbox within
                  24-48 hours
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </MantineProvider>
  );
}
